// The composer's §41.3 choices (shared/API.md): «Solicitar confirmación» and, with «Urgente»,
// «Notificar persistentemente» — two checkable rows under the priority levels, and the line inside
// the box that says what will be asked. One-shot like the level: sending clears them.
import { useEffect, useState } from 'react';
import { data } from '../../../bridge/client';
import { PostAcknowledgements, type AckChoice } from '../../../core/postAcknowledgements';
import type { Level } from '../../../core/messagePriority';
import type { AckConfig } from '../../../data/features/threads';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import type { AckMenu } from '../compose/Priority';
import './threads.css';

export const NO_ACK: AckChoice = { requestAck: false, persistent: false };

let sessionAckConfig: AckConfig | null = null;

/** The server's answer, once per session (a page load is the cache's lifetime, like composeConfig). */
export function useAckConfig(): AckConfig {
  const [config, setConfig] = useState<AckConfig>(sessionAckConfig ?? { postAcknowledgements: false, persistentNotifications: false });
  useEffect(() => {
    if (sessionAckConfig) return;
    let alive = true;
    void data.call('ackConfig').then((c) => {
      sessionAckConfig = c;
      if (alive) setConfig(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return config;
}

/** The menu's model for this composer (null when nothing may be offered — rows hidden, not disabled). */
export function ackMenu(config: AckConfig, level: Level | null, rootId: string | null, value: AckChoice, onChange: (v: AckChoice) => void): AckMenu | undefined {
  const canRequest = PostAcknowledgements.canRequest(config, rootId);
  if (!canRequest) return undefined;
  return { canRequest, canPersist: PostAcknowledgements.canPersist(config, level, rootId), value, onChange };
}

export function AckMenuItems({ ack }: { ack: AckMenu }) {
  return (
    <div className="compose-ack-items" data-ack-menu="">
      <div className="menu-sep" />
      <button
        type="button"
        className="menu-item"
        role="menuitemcheckbox"
        aria-checked={ack.value.requestAck}
        data-ack-choice="request"
        onClick={() => ack.onChange({ ...ack.value, requestAck: !ack.value.requestAck })}
      >
        <Icon name="checkDouble" size={15} />
        {t('web.ack.request')}
        {ack.value.requestAck ? <Icon name="check" size={15} className="compose-check" /> : null}
      </button>
      {ack.canPersist ? (
        <>
          <button
            type="button"
            className="menu-item"
            role="menuitemcheckbox"
            aria-checked={ack.value.persistent}
            data-ack-choice="persistent"
            onClick={() => ack.onChange({ ...ack.value, persistent: !ack.value.persistent })}
          >
            <Icon name="bell" size={15} />
            {t('web.ack.persistent')}
            {ack.value.persistent ? <Icon name="check" size={15} className="compose-check" /> : null}
          </button>
          <div className="compose-ack-footer">{t('web.ack.persistent.footer')}</div>
        </>
      ) : null}
    </div>
  );
}

/** Inside the box, under the priority line: what the send will ask of its readers. */
export function AckLine({ value, level, onClear }: { value: AckChoice; level: Level | null; onClear(): void }) {
  if (!value.requestAck && !(value.persistent && level === 'urgent')) return null;
  const persistent = value.persistent && level === 'urgent';
  return (
    <div className="compose-will-send ack" role="status" data-ack-line="">
      <Icon name="checkDouble" size={13} strokeWidth={2} />
      {persistent ? t('web.ack.willRequestPersistent') : t('web.ack.willRequest')}
      <button type="button" title={t('web.compose.priority.clear')} aria-label={t('web.compose.priority.clear')} onMouseDown={(e) => e.preventDefault()} onClick={onClear}>
        <Icon name="x" size={12} strokeWidth={2.2} />
      </button>
    </div>
  );
}
